import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { contexts } from '../enums/contexts';
import { Action } from '../models/action';
import { GitProvider } from '../models/git-provider';
import { GitRepo } from '../models/git-repo';
import { Project } from '../models/project';
import { Commit, Service } from '../models/service';
import { StackData } from '../models/stack';

export interface ServiceConfigParam {
    internalOnly?: boolean;
}

export interface DomainConfigRequest {
    path: string;
    prefix: string;
    isRoot: boolean;
    serviceId: string;
}

// define token for injection
export const API_SERVICE_TOKEN = new InjectionToken<ApiServiceInterface>(
    'ApiServiceInterface'
);

export interface ApiServiceInterface {
    whoAmI?(): Observable<any>;
    whoAmIKeycloak?(): Observable<any>;
    inviteUserKeycloak?(email: string): Observable<any>;
    listUsersKeycloak?(): Observable<any>;
    connect?(
        provider: string,
        finalPath: string,
        options: { params? }
    ): Observable<any>;
    reportUrlForIframe?(
        service_id: string,
        context: contexts,
        report_id: string
    ): string;
    generateReport?(service_id: string, context: contexts): Observable<any>;
    getRepos?(provider: string): Observable<any>;
    getBranchesOAuth?(repo: GitRepo): Observable<any>;
    getBranches?(service: Service): Observable<any>;
    setBranch?(
        service: Service,
        context: contexts | string,
        branchName: string
    ): Observable<any>;
    setDisplayName?(service: Service, displayName: string): Observable<any>;
    getProject?(project: Project): Observable<any>;
    getTechno?(repo: GitRepo): Observable<any>;
    addProject?(
        projectName: string,
        stack: StackData,
        gitProvider: GitProvider,
        cloudEnv: {
            account?: string;
            region?: string;
        },
        stagingCloudEnv?: {
            accountEmail?: string;
            accountId?: string;
            region: string;
        }
    ): Observable<any>;
    addService?(
        project: Project,
        serviceName: string,
        technoId: number
    ): Observable<any>;
    getS3UrlForFileInput?(action: Action, key: string): Observable<any>;
    sendS3FileFromPrePost?(
        prePost: { url: string; fields: { [key: string]: string } },
        file: File
    ): Observable<any>;
    sendInputs?(
        action: Action,
        inputs: { [key: string]: string }
    ): Observable<any>;
    deploy?(
        service: Service,
        commit: Commit,
        context: string,
        contextCloudEnv?: {
            accountEmail?: string;
            accountId?: string;
            region: string;
        }
    ): Observable<any>;
    getCommits?(
        service: Service,
        context: string,
        firstCommit?: number,
        nbCommitToGet?: number
    ): Observable<any>;
    getActions?(
        service?: Service,
        otherFilter?: { [key: string]: string | number }
    ): Observable<any>;
    getDnsUpdateAction?(project: Project): Observable<any>;
    watchAction?(action: Action): Observable<any>;
    getActionLog?(action: Action): Observable<any>;
    getDiffBranch?(
        service: Service,
        context: contexts,
        origin: string,
        destination: string
    ): Observable<any>;
    mergeBranch?(
        service: Service,
        context: contexts,
        origin: string,
        destination: string
    ): Observable<any>;
    mergeBranchWithCommit?(
        service: Service,
        context: contexts,
        origin: string,
        destination: string,
        commitId: string
    ): Observable<any>;
    getGitWeburl?(service: Service, branchName?: string): Observable<any>;
    getMergeRequestsList?(
        service: Service,
        destination: string
    ): Observable<any>;
    getUrl?(service: Service, context: contexts): Observable<any>;
    getConfigManagerToolUrl?(
        service?: Service,
        context?: contexts
    ): Observable<any>;
    getUsersProfile?(ids: string[]): Observable<any>;
    getAccounts?(cloudProvider?: string): Observable<any>;
    updateAccess?(role: any): Observable<any>;
    addNewDomain?(project: Project, domainName: string): Observable<any>;
    setAsMainDomain?(project: Project, domainName: string): Observable<any>;
    removeDomain?(project: Project, domainName: string): Observable<any>;
    changeDomain?(project: Project, domainName: string): Observable<any>;
    changeServicesDomainConfig?(
        project: Project,
        newDomainConfig: DomainConfigRequest[]
    ): Observable<any>;
    cancelRequest?(project: Project, serviceId: string): Observable<any>;
    cancelDnsUpdateAction?(project: Project): Observable<any>;
    getTemplates?(): Observable<any>;
    getTechnos?(): Observable<any>;
    getService?(service: Service): Observable<any>;
    getServiceConfig?(service: Service): Observable<ServiceConfigParam>;
    replayAction?(action: Action): Observable<any>;
}
